import { Box, Grid } from '@mui/material';
import { TabsTabsCollection, Tab, Tabs as ITabs } from '@/types/generated';
import { IAccordions } from '../Accordions/Accordions';
import { IHalfScreen } from '../HalfScreen/HalfScreen';
import { IMultiCard } from '../MultiCard/MultiCard';
import { IServiceLineHero } from '../ServiceLineHero/ServiceLineHero';
import { ISingleMessage } from '../SingleMessage/SingleMessage';
import TAGTabs from '../TAGTabs';
import TemplateContainer from '../TemplateContainer/TemplateContainer';
import Accordions from '@shared-components/components/Accordions';
import HalfScreen from '@shared-components/components/HalfScreen';
import MultiCard from '@shared-components/components/MultiCard';
import ServiceLineHero from '@shared-components/components/ServiceLineHero';
import SingleMessage from '@shared-components/components/SingleMessage';
import { ContentfulBackground, Maybe, TTabsSize, TTabRowVariant } from '@shared-components/types';
import { textToJumpLink } from '@utils';
export interface ITabsProps extends ITabs {
  /**
   * Background color for the section, and it will also override the background color of the child components.
   * @type { Maybe<ContentfulBackground>}
   * @memberof ITabs
   * @default 'White'
   *
   **/
  backgroundColor?: Maybe<ContentfulBackground>;
  /**
   * Theme for tabs
   * @type {Maybe<TTabsTheme>}
   * @memberof ITabs
   * @default 'Standard'
   **/
  tabRowVariant?: Maybe<TTabRowVariant>;
  /**
   * Tabs contents collection (2-4 items).
   * Could be Accordions
   * @type {TabsTabsCollection}
   * @required
   * @memberof ITabs
   * @example
   **/
  tabsCollection: TabsTabsCollection;
  /**
   * Size for tabs
   * @type {Maybe<TTabsSize>}
   * @memberof ITabs
   * @default 'small'
   **/
  tabsSize?: Maybe<TTabsSize>;
  /**
   * Use for content padding
   * @type {Maybe<boolean>}
   * @memberof ITabs
   * @default false
   **/
  withContentInnerGap?: Maybe<boolean>;
  /**
   * Use for tabs to be sticky or not
   * @type {Maybe<boolean>}
   * @memberof ITabs
   * @default false
   */
  jumplinkTabs?: Maybe<boolean>;
  /**
   * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
   *
   * @type {Maybe<string>}
   * @memberof ITabs
   */
  anchorId?: Maybe<string>;
  /**
   * Variant of the Tabs component
   * @type {Maybe<string>}
   * @memberof ITabs
   * @default 'default'
   **/
  variant?: Maybe<string>;
}
export default function Tabs({
  tabsCollection,
  backgroundColor = 'White',
  tabRowVariant = 'Standard',
  tabsSize = 'Small',
  withContentInnerGap = false,
  jumplinkTabs = false,
  anchorId,
  variant: _variant
}: Readonly<ITabsProps>) {
  const tabsItems = (tabsCollection?.items as Tab[] || []).map((tab, i) => {
    const content = tab?.content ? <Grid item key={i} data-testid={`section_tabs_${i + 1}`}>
				{(() => {
        switch (tab.content?.__typename) {
          case 'Accordions':
            return <Accordions {...tab.content as IAccordions} backgroundColor={backgroundColor} />;
          case 'HalfScreen':
            return <HalfScreen {...tab.content as IHalfScreen} backgroundColor={backgroundColor} />;
          case 'MultiCardV2':
            return <MultiCard {...tab.content as IMultiCard} backgroundColor={backgroundColor} />;
          case 'ServiceLineHero':
            return <ServiceLineHero {...tab.content as IServiceLineHero} backgroundColor={backgroundColor} />;
          case 'SingleMessage':
            return <SingleMessage {...tab.content as ISingleMessage} backgroundColor={backgroundColor} />;
          default:
            return null;
        }
      })()}
			</Grid> : null;
    return {
      label: tab?.label ?? '',
      content,
      value: tab.sys.id,
      jumplinkName: textToJumpLink(tab.jumplinkName)
    };
  });
  return <TemplateContainer {...anchorId && {
    id: textToJumpLink(anchorId)
  }} data-sentry-element="TemplateContainer" data-sentry-component="Tabs" data-sentry-source-file="Tabs.tsx">
			<Box data-testid="section_tabs" display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{
      background: anchorId === 'aspen-dental' ? 'linear-gradient(to bottom, #FFFFFF 10%, #F7FFFF 30%)' : null
    }} data-sentry-element="Box" data-sentry-source-file="Tabs.tsx">
				<TAGTabs tabs={tabsItems} dataTestId="tabs" tabRowVariant={tabRowVariant ?? 'Standard'} tabsSize={tabsSize ?? 'Small'} withContentInnerGap={withContentInnerGap ?? false} role={jumplinkTabs ? 'navigation' : 'default'} jumplinkTabs={jumplinkTabs} data-sentry-element="TAGTabs" data-sentry-source-file="Tabs.tsx" />
			</Box>
		</TemplateContainer>;
}